import React, { useEffect } from 'react';

import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Controller, useFormContext } from 'react-hook-form';

import { ActionIcon, Icon, Input, TextInput as MTextInput } from '@tf/ui';

import { useDisabledAutocompleteAttributes } from '../../../hooks';
import { TooltipIcon } from '../TooltipIcon';
import type { DefaultInputProps } from '../types';

export default function TextInput({
	name,
	label,
	description,
	placeholder,
	isRequired,
	isReadOnly,
	className,
	tooltip,
	link,
}: DefaultInputProps & { link?: boolean }) {
	const { _ } = useLingui();
	const { control, setValue, getValues } = useFormContext();
	const disabledAutocompleteAttributes = useDisabledAutocompleteAttributes();

	useEffect(() => {
		const value = getValues(name);
		setValue(name, value || '');
	}, []);

	return (
		<Controller
			control={control}
			name={name}
			render={({ field: { onChange, onBlur, value = '', ref }, fieldState: { error } }) => (
				<Input.Wrapper
					className={className}
					id={name}
					label={label}
					labelProps={{ required: isRequired }}
				>
					<MTextInput
						id={name}
						value={value}
						onChange={onChange}
						onBlur={onBlur}
						ref={ref}
						placeholder={
							isReadOnly
								? _(
										msg({
											id: 'fb.textInput.empty',
											message: 'Not specified',
										})
								  )
								: placeholder ||
								  _(
										msg({
											id: 'fb.textInput.placeholder',
											message: 'Enter value',
										})
								  )
						}
						description={description}
						readOnly={isReadOnly}
						data-readonly={isReadOnly}
						error={Boolean(error)}
						rightSection={
							tooltip || error ? (
								<TooltipIcon tooltip={tooltip} error={error} />
							) : link && Boolean(value) ? (
								<ActionIcon variant="light">
									<Icon.IconLink
										size={18}
										onClick={() => {
											const fullUrl = /^[a-z]+:/.test(value) ? value : `https://${value}`;
											window.open(fullUrl, '_blank');
										}}
									/>
								</ActionIcon>
							) : undefined
						}
						{...disabledAutocompleteAttributes}
					/>
				</Input.Wrapper>
			)}
		/>
	);
}
